import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Loader2, Monitor } from 'lucide-react'
import type { BPlateDetail, BPlateView, BPlateViewColumnDef, BPlateChartType } from '../types'
import { ChartView, type JourneyApi } from '../components/PlateViewRenderer'
import { EnergyOSDeviceHouse } from '../components/EnergyOSDeviceHouse'
import { EnergyOSPlanTimeline } from '../components/EnergyOSPlanTimeline'
import { AddEventModal } from '../components/AddEventModal'
import { useJourneyTrigger } from '../hooks/useJourneyTrigger'

type RunViewFn = (viewId: string, inputs: Record<string, string>) => Promise<{ data: unknown; error?: string }>

function findView(plate: BPlateDetail, sectionTitle: string, viewTitle: string): BPlateView | undefined {
  return plate.sections.find(s => s.title === sectionTitle)?.views.find(v => v.title === viewTitle)
}

// Refetches every 30s so the house's device animations and today's plan feel genuinely live,
// without the visitor needing to manually reload the page.
function useViewData(runView: RunViewFn, view: BPlateView | undefined, runInputs: Record<string, string>) {
  return useQuery({
    queryKey: ['eos-dashboard-view', view?.id, runInputs],
    queryFn: () => runView(view!.id, runInputs),
    enabled: !!view,
    retry: false,
    refetchInterval: 30_000,
  })
}

interface Props {
  plate: BPlateDetail
  runInputs: Record<string, string>
  runView: RunViewFn
  journeyApi: JourneyApi
}

// Bespoke, EnergyOS-specific rendering of the Home Dashboard plate — OpenPlaceTokenView.tsx
// branches here instead of the generic PlateLayout for this one plate by name, so every other
// business's OpenPlace page stays fully generic. Reads the same view data through the same
// public token API PlateLayout would; only the presentation is specialized (house illustration
// with animated device icons, a real timeline, Emergency Stop as a prominent top-bar action, Add
// Event as a modal instead of an always-visible inline form) per the user's own design brief.
export function EnergyOSHomeDashboard({ plate, runInputs, runView, journeyApi }: Props) {
  const overviewView = findView(plate, 'Overview', 'Overview')
  const stopView = findView(plate, 'Overview', 'Emergency Stop')
  const devicesView = findView(plate, 'Devices', 'Devices')
  const planView = findView(plate, "Today's Plan", "Today's Plan")
  const forecastView = findView(plate, 'Forecast', 'Forecast')
  const eventsView = findView(plate, 'Events', 'Events')
  const addEventView = findView(plate, 'Events', 'Add Event')

  const { data: overview } = useViewData(runView, overviewView, runInputs)
  const { data: devices } = useViewData(runView, devicesView, runInputs)
  const { data: plan } = useViewData(runView, planView, runInputs)
  const { data: forecast } = useViewData(runView, forecastView, runInputs)
  const { data: events } = useViewData(runView, eventsView, runInputs)

  const stop = useJourneyTrigger(journeyApi)
  const [showAddEvent, setShowAddEvent] = useState(false)

  const overviewData = (overview?.data ?? {}) as Record<string, unknown>
  const deviceRows = (devices?.data ?? []) as Record<string, unknown>[]
  const planData = (plan?.data ?? {}) as { plan?: { status?: unknown }; actions?: Record<string, unknown>[] }
  const eventRows = ((events?.data as Record<string, unknown> | undefined)?.['EnergyOS Event'] ?? []) as Record<string, unknown>[]

  const handleStop = () => {
    if (!stopView?.preparationId) return
    if (!window.confirm("Stop all automation for this home for the next hour? Today's plan will be marked stopped.")) return
    stop.run(stopView.preparationId, runInputs)
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-card">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">R</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 leading-tight">{String(overviewData.mpid ?? plate.name)}</p>
              <p className="text-theme-xs text-slate-400 leading-tight">EnergyOS dashboard</p>
            </div>
          </div>

          {stopView?.preparationId && (
            <button onClick={handleStop} disabled={stop.isBusy} className="btn-danger">
              {stop.isBusy ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              {stop.status === 'completed' ? 'Stopped' : 'Emergency Stop'}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <OverviewStrip data={overviewData} columnConfig={overviewView?.columnConfig} />

        {devicesView && (
          <section className="card p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-4">Devices</h2>
            <EnergyOSDeviceHouse
              devices={deviceRows}
              rowActions={devicesView.rowActions ?? []}
              runInputs={runInputs}
              journeyApi={journeyApi}
            />
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {planView && (
            <section className="card p-5">
              <h2 className="text-sm font-semibold text-slate-800 mb-1">Today's Plan</h2>
              <EnergyOSPlanTimeline plan={planData.plan} actions={planData.actions ?? []} />
            </section>
          )}

          {forecastView && <ForecastCard view={forecastView} data={forecast?.data} />}
        </div>

        {eventsView && (
          <section className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-800">Events</h2>
              {addEventView?.preparationId && (
                <button onClick={() => setShowAddEvent(true)} className="btn-secondary text-xs">+ Add Event</button>
              )}
            </div>
            <EventsList events={eventRows} />
          </section>
        )}
      </main>

      {showAddEvent && addEventView && (
        <AddEventModal view={addEventView} runInputs={runInputs} journeyApi={journeyApi} onClose={() => setShowAddEvent(false)} />
      )}

      <div className="flex items-center justify-center gap-1.5 text-theme-xs text-slate-300 py-4">
        <Monitor size={11} /> OpenPlace
      </div>
    </div>
  )
}

function OverviewStrip({ data, columnConfig }: { data: Record<string, unknown>; columnConfig?: BPlateViewColumnDef[] }) {
  const configMap = new Map(columnConfig?.map(c => [c.key, c]) ?? [])
  const entries = Object.entries(data)
  if (entries.length === 0) return null
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {entries.map(([key, value]) => {
        const def = configMap.get(key)
        return (
          <div key={key} className="card p-4" title={def?.description}>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1 truncate">{def?.label ?? key}</p>
            <p className="text-xl font-semibold text-slate-900 truncate">{String(value)}</p>
          </div>
        )
      })}
    </div>
  )
}

function ForecastCard({ view, data }: { view: BPlateView; data: unknown }) {
  const [chartType, setChartType] = useState<BPlateChartType>(view.chartType)
  return (
    <section className="card p-5">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">Forecast</h2>
      <ChartView data={data} chartType={chartType} onChartTypeChange={setChartType} />
    </section>
  )
}

function EventsList({ events }: { events: Record<string, unknown>[] }) {
  if (events.length === 0) return <p className="text-xs text-slate-400 text-center py-6">No events yet</p>
  return (
    <div className="divide-y divide-slate-100">
      {events.map((e, i) => (
        <div key={i} className="py-2.5 flex items-center justify-between text-sm gap-3">
          <div className="min-w-0">
            <p className="font-medium text-slate-800 truncate">{String(e['EnergyOS Event.eventType'] ?? '—')}</p>
            {!!e['EnergyOS Event.note'] && <p className="text-theme-xs text-slate-400 truncate">{String(e['EnergyOS Event.note'])}</p>}
          </div>
          <div className="text-theme-xs text-slate-500 text-right shrink-0">
            <p>{String(e['EnergyOS Event.startTime'] ?? '')}</p>
            <p>{String(e['EnergyOS Event.endTime'] ?? '')}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
